// Social (Phase 4): the LinkedIn weave. Import a batch (one blog's posts
// become a series), the round-robin scheduler assigns one post per
// weekday across active series, and the woven schedule shows the next
// weeks with per-series color. Re-weave happens automatically on import;
// manual date edits pin a post and the weave flows around it.

import { useMemo, useState } from 'react';
import { useMarketing } from '../MarketingContext';
import { apiFetch } from '../../../utils/apiFetch';
import { parsePostsCsv } from '../social/csv';
import { parseLocalDate } from '../dates';
import { Card, Eyebrow, Pill } from '../ui';
import SocialPostModal from '../social/SocialPostModal';

// Per-series badge colors, assigned by series index (stable because the
// series list is ordered by id).
const SERIES_TONES = [
  'bg-bullogic-blue/14 text-bullogic-blue',
  'bg-deep-teal/13 text-deep-teal',
  'bg-amber/15 text-amber',
  'bg-navy/10 text-navy',
  'bg-rose/13 text-[#d63a54]',
  'bg-sky-blue/20 text-navy',
];
const SERIES_EDGES = ['#2596BE', '#006D78', '#B7791F', '#022889', '#FF6E83', '#3EAAD0'];

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export default function SocialView() {
  const { social, reloadSocial } = useMarketing();
  const [busyId, setBusyId] = useState(null);
  const [openPost, setOpenPost] = useState(null);
  const [err, setErr] = useState(null);

  const series = social.data?.series || [];
  const posts = social.data?.posts || [];
  const active = series.filter(s => s.remaining > 0);
  const toneOf = useMemo(() => {
    const map = {};
    series.forEach((s, i) => { map[s.id] = { tone: SERIES_TONES[i % SERIES_TONES.length], edge: SERIES_EDGES[i % SERIES_EDGES.length], letter: LETTERS[i % 26] }; });
    return map;
  }, [series]);

  // Group the schedule by ISO week (Monday key).
  const weeks = useMemo(() => {
    const byWeek = new Map();
    for (const p of posts) {
      if (!p.scheduledDate) continue;
      const d = parseLocalDate(p.scheduledDate);
      const monday = new Date(d);
      monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
      const key = monday.toDateString();
      if (!byWeek.has(key)) byWeek.set(key, { monday, posts: [] });
      byWeek.get(key).posts.push(p);
    }
    return [...byWeek.values()].sort((a, b) => a.monday - b.monday);
  }, [posts]);

  async function markPosted(post) {
    setBusyId(post.id);
    setErr(null);
    try {
      const res = await apiFetch(`/api/data/social/posts/${post.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'published' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await reloadSocial();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function reweave() {
    setBusyId('reweave');
    setErr(null);
    try {
      const res = await apiFetch('/api/data/social/reweave', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await reloadSocial();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div className="flex flex-col gap-4">
      <ImportCard onImported={reloadSocial} />

      <Card className="p-4">
        <div className="flex items-center justify-between mb-2.5">
          <Eyebrow>Series in rotation · posting Mon-Fri</Eyebrow>
          <button
            onClick={reweave}
            disabled={busyId === 'reweave'}
            className="text-bullogic-blue text-[11px] font-black border border-bullogic-blue/40 rounded-[6px] px-3 py-1.5 hover:bg-bullogic-blue/5 disabled:opacity-50"
          >
            {busyId === 'reweave' ? 'Weaving…' : 'Re-weave from today'}
          </button>
        </div>

        {social.loading && <div className="text-[12px] text-ink-3">Loading series…</div>}
        {!social.loading && active.length === 0 && (
          <div className="text-[12.5px] text-ink-2">No series in rotation. Import a batch above to start the weave.</div>
        )}

        <div className="flex gap-2.5 flex-wrap">
          {active.map(s => (
            <div
              key={s.id}
              className="bg-white border border-line rounded-[7px] px-3 py-2 shadow-[0_1px_3px_rgba(2,10,40,0.06)]"
              style={{ borderLeft: `3px solid ${toneOf[s.id]?.edge}` }}
            >
              <b className="block text-[12.5px] text-navy">{toneOf[s.id]?.letter} · {s.name}</b>
              <span className="text-[11px] text-ink-2 font-semibold">{s.remaining} of {s.total} posts left</span>
            </div>
          ))}
        </div>

        <div className="mt-3 bg-bullogic-blue/5 border border-dashed border-bullogic-blue/40 rounded-[7px] px-3 py-2 text-[12px] text-ink-2">
          <b className="text-navy">Weave, don't dump.</b> One post per weekday, rotating across active series,
          so a single blog stretches across weeks instead of burning out in five days.
          A new import <b className="text-navy">leads the rotation</b> and starts on the next open
          weekday, while the backlog keeps its one slot per cycle. Editing a post's date pins it.
        </div>
      </Card>

      {err && <div className="text-[12px] text-rose">{err}</div>}

      {weeks.map(({ monday, posts: weekPosts }) => (
        <Card key={monday.toDateString()} className="p-4">
          <Eyebrow className="mb-2.5 text-ink-3">
            Week of {monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </Eyebrow>
          <div className="flex flex-col gap-1.5">
            {weekPosts.map(p => {
              const d = parseLocalDate(p.scheduledDate);
              const isToday = d.getTime() === today.getTime();
              const t = toneOf[p.seriesId] || {};
              return (
                <div
                  key={p.id}
                  className={`grid grid-cols-[64px_1fr_auto] gap-3 items-center px-2.5 py-2 rounded-[7px] ${
                    isToday ? 'bg-bullogic-blue/10' : 'bg-[#faf9f6]'
                  }`}
                >
                  <span className={`text-[11px] font-black uppercase ${isToday ? 'text-bullogic-blue' : 'text-ink-3'}`}>
                    {d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
                  </span>
                  <button
                    onClick={() => setOpenPost(p)}
                    title="Open post (copy + mark posted)"
                    className={`text-left text-[12.5px] hover:underline decoration-bullogic-blue/50 underline-offset-2 ${p.status === 'published' ? 'text-ink-3 line-through' : 'text-ink'}`}
                  >
                    {p.title || firstLine(p.body)}
                  </button>
                  <span className="flex items-center gap-2">
                    <span className={`text-[10px] font-black px-2 py-px rounded-[5px] whitespace-nowrap ${t.tone || ''}`}>
                      {t.letter} · {shortName(p.seriesName)}
                    </span>
                    {p.pinned && <Pill tone="neutral" className="text-[9px] px-1.5">pinned</Pill>}
                    <CopyBtn text={p.body} label="Copy" />
                    {p.firstComment && <CopyBtn text={p.firstComment} label="Comment" />}
                    {p.status !== 'published' && (
                      <button
                        onClick={() => markPosted(p)}
                        disabled={busyId === p.id}
                        title="Mark posted"
                        className="w-[15px] h-[15px] rounded-[4px] border-[1.6px] border-ink-3 hover:border-deep-teal bg-white"
                      />
                    )}
                    {p.status === 'published' && (
                      <span className="w-[15px] h-[15px] rounded-[4px] bg-deep-teal text-white text-[10px] leading-[13px] text-center">✓</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      {!social.loading && weeks.length === 0 && active.length > 0 && (
        <div className="text-[12.5px] text-ink-2">Nothing scheduled in the next weeks. Hit "Re-weave from today".</div>
      )}

      <SocialPostModal post={openPost} onClose={() => setOpenPost(null)} />
    </div>
  );
}

function firstLine(body) {
  const line = (body || '').split('\n').find(l => l.trim());
  return line?.length > 90 ? line.slice(0, 87) + '…' : line || '(empty)';
}

function shortName(name) {
  return (name || '').length > 22 ? name.slice(0, 20) + '…' : name;
}

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch { /* clipboard unavailable */ }
      }}
      className="text-[10px] font-bold text-ink-2 border border-line rounded-[5px] px-1.5 py-px hover:text-navy hover:border-ink-3"
    >
      {copied ? '✓' : label}
    </button>
  );
}

function ImportCard({ onImported }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function handleParse(text) {
    setCsv(text);
    if (!text.trim()) { setPreview(null); setErr(null); return; }
    const { posts, error } = parsePostsCsv(text);
    setErr(error || null);
    setPreview(error ? null : posts);
  }

  async function submit() {
    if (!preview?.length || !name.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await apiFetch('/api/data/social/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), posts: preview }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setOpen(false); setName(''); setCsv(''); setPreview(null);
      await onImported();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card lash="sky" className="p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Eyebrow>Import batch</Eyebrow>
          <div className="text-[13px] text-ink mt-1">
            Paste the generated CSV. The posts become a <b>series</b> that joins the rotation and re-weaves the schedule.
          </div>
        </div>
        <button
          onClick={() => setOpen(v => !v)}
          className="text-bullogic-blue text-[12px] font-black border border-bullogic-blue/40 rounded-[6px] px-3.5 py-2 hover:bg-bullogic-blue/5 whitespace-nowrap"
        >
          {open ? 'Close' : 'Import series →'}
        </button>
      </div>

      {open && (
        <div className="mt-3 border-t border-line-2 pt-3">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder='Series name, e.g. "Entity structures"'
            className="w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] mb-2 focus:outline-none focus:border-bullogic-blue"
          />
          <textarea
            value={csv}
            onChange={e => handleParse(e.target.value)}
            rows={6}
            placeholder={'CSV with a header row. Columns: body (required), first_comment, hook, title.\nQuoted multi-line bodies are fine.'}
            className="w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12px] font-mono focus:outline-none focus:border-bullogic-blue"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[12px] text-ink-2">
              {err ? <span className="text-rose">{err}</span>
                : preview ? `${preview.length} posts parsed` : 'Waiting for CSV…'}
            </span>
            <button
              onClick={submit}
              disabled={saving || !preview?.length || !name.trim()}
              className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-4 py-1.5 disabled:opacity-50"
            >
              {saving ? 'Importing…' : `Import ${preview?.length || 0} posts & weave`}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
